/**
 * Client API type-safe per le chiamate AJAX
 * Sostituisce le chiamate fetch sparse nel codice legacy
 */

export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  error?: string;
  data?: T;
  errors?: Record<string, string>;
  redirect_url?: string;
}

export interface RequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: FormData | string | Record<string, any> | null;
  timeout?: number;
}

/**
 * Client API centralizzato con gestione errori e CSRF
 */
export class ApiClient {
  private static instance: ApiClient;
  private csrfToken: string | null = null;

  private constructor() {
    this.initializeCsrfToken();
  }

  static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  /**
   * Inizializza il token CSRF dal DOM
   */
  private initializeCsrfToken() {
    const csrfInput = document.querySelector('[name=csrfmiddlewaretoken]') as HTMLInputElement;
    if (csrfInput) {
      this.csrfToken = csrfInput.value;
    }
  }

  /**
   * Effettua una chiamata API type-safe
   */
  async request<T = any>(url: string, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    const {
      method = 'POST',
      headers = {},
      body,
      timeout = 30000
    } = config;

    // Configurazione headers di default
    const defaultHeaders: Record<string, string> = {
      'X-Requested-With': 'XMLHttpRequest',
      ...headers
    };

    // Aggiungi CSRF token se necessario
    if (method !== 'GET' && this.csrfToken) {
      defaultHeaders['X-CSRFToken'] = this.csrfToken;
    }

    // Prepara il body
    let requestBody: BodyInit | null = null;
    if (body instanceof FormData) {
      requestBody = body;
      // Aggiungi CSRF token al FormData se non presente
      if (!body.has('csrfmiddlewaretoken') && this.csrfToken) {
        body.append('csrfmiddlewaretoken', this.csrfToken);
      }
    } else if (typeof body === 'string') {
      requestBody = body;
      defaultHeaders['Content-Type'] = 'application/x-www-form-urlencoded';
    } else if (body && typeof body === 'object') {
      requestBody = JSON.stringify(body);
      defaultHeaders['Content-Type'] = 'application/json';
    }

    // Controller per timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        method,
        headers: defaultHeaders,
        body: requestBody,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // Parsing della risposta
      let responseData: ApiResponse<T>;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        // Fallback per risposte non-JSON
        const text = await response.text();
        responseData = {
          success: response.ok,
          message: text || (response.ok ? 'Operazione completata' : 'Errore del server'),
          data: text as unknown as T
        };
      }

      return responseData;

    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          return {
            success: false,
            message: 'Timeout della richiesta',
            errors: { general: 'La richiesta ha impiegato troppo tempo' }
          };
        }
      }

      console.error('API request failed:', error);
      return {
        success: false,
        message: 'Errore di connessione',
        errors: { general: 'Impossibile comunicare con il server' }
      };
    }
  }

  /**
   * Shortcut per GET requests
   */
  async get<T = any>(url: string, config: Omit<RequestConfig, 'method' | 'body'> = {}): Promise<ApiResponse<T>> {
    return this.request<T>(url, { ...config, method: 'GET' });
  }

  /**
   * Shortcut per POST requests
   */
  async post<T = any>(url: string, body: RequestConfig['body'] = null, config: Omit<RequestConfig, 'method' | 'body'> = {}): Promise<ApiResponse<T>> {
    return this.request<T>(url, { ...config, method: 'POST', body });
  }

  /**
   * Aggiorna il token CSRF
   */
  updateCsrfToken(token: string) {
    this.csrfToken = token;
  }

  /**
   * Ottiene il token CSRF corrente
   */
  getCsrfToken(): string | null {
    return this.csrfToken;
  }
}

// Esporta l'istanza singleton
export const apiClient = ApiClient.getInstance();

/**
 * API specifiche per funzionalità applicative
 */
import type { EditMatchPairsData, EditMatchPairsResponse } from '../types/match';

/**
 * Modifica le coppie di un match
 */
export async function editMatchPairs(
  tournamentPk: string,
  data: EditMatchPairsData
): Promise<EditMatchPairsResponse> {
  // Validate tournamentPk
  if (!tournamentPk || tournamentPk === '' || tournamentPk === 'undefined') {
    throw new Error('Tournament ID is required');
  }
  
  const url = `/simulations/tournaments/${tournamentPk}/edit-match-pairs/`;
  const formData = new FormData();
  
  // Converti i dati in FormData
  Object.entries(data).forEach(([key, value]) => {
    formData.append(key, value.toString());
  });
  
  const response = await apiClient.post<EditMatchPairsResponse>(url, formData);
  
  // Debug: log della risposta
  console.log('🔍 Edit match teams response:', response);
  
  if (!response.success) {
    throw new Error(response.message || response.error || 'Impossibile modificare le coppie');
  }
  
  return response.data || response as any;
}